export const CONTENT_TYPE_JSON = 'application/json'
// pathways-api's router (easyroute) decodes a JSON body only when the Content-Type is this value,
// ignoring case; with plain application/json, or a space after the semicolon, its handlers see an empty body.
export const PATHWAYS_JSON_CONTENT_TYPE = 'application/json;charset=utf-8'

export class HTTPError extends Error {
  public readonly response: Response
  constructor(message: string, response: Response, options?: ErrorOptions, ) {
    super(message, options);
    this.response = response
  }
}

type httpBodyArgs = {
  [key: string]: unknown
}
type formBodyArgs = {
  [key: string]: string | Blob
}

type httpRequestArgs = {
  url: string,
  contentType?: string,
  method?: string,
  urlParams?: string | Record<string, string> | string[][] | URLSearchParams | undefined,
  jsonBody?: httpBodyArgs | undefined,
  formData?: formBodyArgs | undefined,
  urlEncodedForm?: Record<string, string>,
  includeCredentials?: boolean,
  addlHeaders?: Record<string, string>,
  signal?: AbortSignal
}

export async function sendHTTPRequest({ url, method = 'GET',  urlParams, jsonBody, formData, urlEncodedForm, contentType, includeCredentials = true, addlHeaders, signal }: httpRequestArgs): Promise<Response> {
  if (urlParams) {
    url += '?' + new URLSearchParams(urlParams).toString()
  }

  const myHeaders = new Headers()
  myHeaders.append('X-Requested-With','XMLHttpRequest')
  const contentTypeHeader = getContentTypeFromArgs({ url, contentType, jsonBody, urlParams })
  if (contentTypeHeader) {
    myHeaders.append('Content-Type', contentTypeHeader)
  }

  for (const key in addlHeaders) {
    myHeaders.append(key, addlHeaders[key])
  }

  const options: RequestInit = { method, headers: myHeaders, signal }
  if (includeCredentials) {
    options.credentials = 'include'
  }
  if (jsonBody) {
    options.body = JSON.stringify(jsonBody)
  }
  if (formData) {
    const bodyFormData = new FormData()
    for (const [key, value] of Object.entries(formData)) {
      bodyFormData.append(key, value);
    }
    options.body = bodyFormData
  }
  if (urlEncodedForm) {
    options.body = new URLSearchParams(urlEncodedForm)
  }

  const response = await fetch(url, options)

  if (!response.ok) {
    throw new HTTPError(`${response.status} status response from HTTP request`, response)
  }

  return response
}

function getContentTypeFromArgs(args: httpRequestArgs): string | undefined {
  if (args.contentType) {
    return args.contentType
  }
  if (args.jsonBody) {
    return CONTENT_TYPE_JSON
  }
  return undefined
}

/**
 * Safely parse JSON response with fallback for empty/non-JSON responses
 * Returns parsed JSON data on success, or { error: text } on failure
 */
export async function safeJsonParse(response: Response): Promise<any> {
  try {
    return await response.json()
  } catch {
    // If JSON parsing fails, return error object
    const text = await response.text()
    return { error: text || response.statusText }
  }
}